import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const empty = { name: "", email: "", mobile: "", notes: "", address: "" };

const fmtShortDate = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return null;
    }
};

const pickDates = (jobs) => {
    if (!jobs || jobs.length === 0) return { installed: null, due: null };
    // Installed = earliest scheduled_date (imports set this to install_date)
    // Due = latest next_due_date across their jobs
    let installed = null;
    let due = null;
    for (const j of jobs) {
        if (j.scheduled_date && (!installed || j.scheduled_date < installed)) {
            installed = j.scheduled_date;
        }
        if (j.next_due_date && (!due || j.next_due_date > due)) {
            due = j.next_due_date;
        }
    }
    return { installed: fmtShortDate(installed), due: fmtShortDate(due) };
};

const Customers = () => {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(empty);
    const [editingId, setEditingId] = useState(null);
    const [editProperties, setEditProperties] = useState([]); // properties of the customer being edited
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [propertiesByCustomer, setPropertiesByCustomer] = useState({});
    const [jobsByCustomer, setJobsByCustomer] = useState({});

    const load = (query = "") => {
        setLoading(true);
        return Promise.all([
            api.get("/customers", { params: query ? { q: query } : {} }),
            api.get("/properties"),
            api.get("/jobs"),
        ])
            .then(([c, p, j]) => {
                setItems(c.data);
                const propMap = {};
                const propToCustomer = {};
                for (const prop of p.data) {
                    if (!propMap[prop.customer_id]) propMap[prop.customer_id] = [];
                    propMap[prop.customer_id].push(prop);
                    propToCustomer[prop.id] = prop.customer_id;
                }
                setPropertiesByCustomer(propMap);
                const jobMap = {};
                for (const job of j.data) {
                    const cid = propToCustomer[job.property_id];
                    if (!cid) continue;
                    if (!jobMap[cid]) jobMap[cid] = [];
                    jobMap[cid].push(job);
                }
                setJobsByCustomer(jobMap);
            })
            .catch(() => toast.error("Failed to load customers"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);
    useEffect(() => {
        const t = setTimeout(() => load(q), 250);
        return () => clearTimeout(t);
    }, [q]);

    const openCreate = () => {
        setForm(empty);
        setEditingId(null);
        setEditProperties([]);
        setOpen(true);
    };
    const openEdit = async (c) => {
        setForm({
            name: c.name || "",
            email: c.email || "",
            mobile: c.mobile || "",
            notes: c.notes || "",
            address: "",
        });
        setEditingId(c.id);
        setEditProperties([]);
        setOpen(true);
        // Fetch this customer's properties so we can offer inline edit for the address(es)
        try {
            const r = await api.get("/properties", { params: { customer_id: c.id } });
            setEditProperties(r.data);
            if (r.data.length === 1) {
                setForm((f) => ({ ...f, address: r.data[0].address || "" }));
            }
        } catch {
            /* non-fatal */
        }
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                email: form.email.trim() || null,
                mobile: form.mobile.trim() || null,
                notes: form.notes.trim() || null,
            };
            const address = form.address.trim();
            if (editingId) {
                await api.put(`/customers/${editingId}`, payload);
                // Address handling on edit: only when 0 or 1 property
                if (editProperties.length <= 1) {
                    if (editProperties.length === 1) {
                        // Update existing property if address changed
                        if (address && address !== (editProperties[0].address || "")) {
                            await api.put(`/properties/${editProperties[0].id}`, {
                                customer_id: editingId,
                                address,
                            });
                        }
                    } else if (address) {
                        // No property yet — create one
                        await api.post("/properties", { customer_id: editingId, address });
                    }
                }
                toast.success("Customer updated");
            } else {
                const r = await api.post("/customers", payload);
                if (address) {
                    try {
                        await api.post("/properties", {
                            customer_id: r.data.id,
                            address,
                        });
                    } catch {
                        toast.warning("Customer saved but couldn't add the property — add it manually");
                    }
                }
                toast.success("Customer created");
            }
            setOpen(false);
            load(q);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/customers/${id}`);
            toast.success("Customer deleted");
            load(q);
        } catch {
            toast.error("Delete failed");
        }
    };

    return (
        <div className="space-y-6" data-testid="customers-page">
            <div className="flex flex-wrap items-end gap-4 justify-between">
                <div>
                    <p className="text-label">People</p>
                    <h2 className="font-display font-black text-4xl uppercase tracking-tight leading-none">
                        Customers
                    </h2>
                    <p className="text-neutral-600 mt-2">
                        {items.length} record{items.length === 1 ? "" : "s"}
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <button data-testid="new-customer-btn" onClick={openCreate} className="dh-btn">
                            <Plus size={18} /> New customer
                        </button>
                    </DialogTrigger>
                    <DialogContent className="bg-white border-2 border-[color:var(--dh-line)] rounded-none shadow-[6px_6px_0_var(--dh-line)]">
                        <DialogHeader>
                            <DialogTitle className="font-display uppercase tracking-tight text-2xl">
                                {editingId ? "Edit customer" : "New customer"}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={save} className="space-y-4" data-testid="customer-form">
                            <div>
                                <label className="text-label block mb-1">Name</label>
                                <input
                                    className="dh-input"
                                    data-testid="customer-name-input"
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-label block mb-1">Email</label>
                                    <input
                                        type="email"
                                        className="dh-input"
                                        data-testid="customer-email-input"
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="text-label block mb-1">Mobile</label>
                                    <input
                                        className="dh-input"
                                        data-testid="customer-mobile-input"
                                        value={form.mobile}
                                        onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-label block mb-1">Notes</label>
                                <textarea
                                    className="dh-input"
                                    data-testid="customer-notes-input"
                                    rows={3}
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                />
                            </div>
                            {!editingId && (
                                <div>
                                    <label className="text-label block mb-1">
                                        Property address <span className="text-neutral-400 normal-case">(optional)</span>
                                    </label>
                                    <textarea
                                        className="dh-input"
                                        data-testid="customer-address-input"
                                        rows={2}
                                        placeholder="e.g. 3 Yorke Gardens, Reigate, RH2 9HQ"
                                        value={form.address}
                                        onChange={(e) => setForm({ ...form, address: e.target.value })}
                                    />
                                    <p className="text-xs text-neutral-500 mt-1 font-mono">
                                        Adds their first property in one go. You can add more later from the
                                        customer&apos;s page.
                                    </p>
                                </div>
                            )}
                            {editingId && editProperties.length <= 1 && (
                                <div>
                                    <label className="text-label block mb-1">
                                        Property address{" "}
                                        <span className="text-neutral-400 normal-case">
                                            {editProperties.length === 0 ? "(none yet)" : ""}
                                        </span>
                                    </label>
                                    <textarea
                                        className="dh-input"
                                        data-testid="customer-address-input"
                                        rows={2}
                                        placeholder="e.g. 3 Yorke Gardens, Reigate, RH2 9HQ"
                                        value={form.address}
                                        onChange={(e) => setForm({ ...form, address: e.target.value })}
                                    />
                                    <p className="text-xs text-neutral-500 mt-1 font-mono">
                                        {editProperties.length === 0
                                            ? "No property on file yet — add one here to save a step."
                                            : "Edits go straight to their property record."}
                                    </p>
                                </div>
                            )}
                            {editingId && editProperties.length > 1 && (
                                <div className="border-2 border-[color:var(--dh-line)] bg-[color:var(--dh-paper-2)] p-3">
                                    <p className="text-label mb-1">Properties</p>
                                    <p className="text-sm">
                                        This customer has <strong>{editProperties.length}</strong> properties.
                                        Open their page to edit individual addresses.
                                    </p>
                                    <ul className="mt-2 space-y-0.5 text-xs font-mono text-neutral-600">
                                        {editProperties.map((p) => (
                                            <li key={p.id}>· {p.address}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <DialogFooter>
                                <button
                                    data-testid="customer-save-btn"
                                    type="submit"
                                    className="dh-btn"
                                    disabled={saving}
                                >
                                    {saving ? "Saving…" : "Save"}
                                </button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="dh-card p-3 flex items-center gap-3">
                <Search size={18} className="ml-1" />
                <input
                    data-testid="customer-search"
                    className="flex-1 outline-none bg-transparent h-10 text-base"
                    placeholder="Search by name, email, mobile…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
            </div>

            {loading ? (
                <p className="text-label">Loading…</p>
            ) : items.length === 0 ? (
                <div className="dh-card p-8 text-center">
                    <p className="font-display uppercase font-bold text-xl">No customers yet</p>
                </div>
            ) : (
                <ul className="space-y-2" data-testid="customer-list">
                    {items.map((c) => {
                        const props = propertiesByCustomer[c.id] || [];
                        const { installed, due } = pickDates(jobsByCustomer[c.id]);
                        return (
                        <li
                            key={c.id}
                            data-testid={`customer-row-${c.id}`}
                            className="dh-card p-4 flex flex-col gap-3"
                        >
                            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                            <Link
                                to={`/customers/${c.id}`}
                                className="flex-1 min-w-0"
                                data-testid={`customer-open-${c.id}`}
                            >
                                <p className="font-display font-black uppercase text-lg leading-tight truncate">
                                    {c.name}
                                </p>
                                <p className="text-sm text-neutral-600 truncate font-mono">
                                    {c.email || "no email"} · {c.mobile || "no phone"}
                                </p>
                            </Link>
                            <div
                                className="hidden sm:block max-w-[240px] text-right shrink-0"
                                data-testid={`customer-address-${c.id}`}
                            >
                                <p className="text-[10px] tracking-widest uppercase font-bold text-neutral-500">
                                    {props.length > 1 ? `${props.length} properties` : "Address"}
                                </p>
                                <p className="text-sm text-neutral-800 leading-tight line-clamp-2">
                                    {props[0]?.address || <span className="text-neutral-400">—</span>}
                                </p>
                            </div>
                            {props.length > 0 && (
                                <p
                                    className="sm:hidden text-xs text-neutral-600 truncate w-full"
                                    data-testid={`customer-address-mobile-${c.id}`}
                                >
                                    {props[0].address}
                                    {props.length > 1 ? ` +${props.length - 1} more` : ""}
                                </p>
                            )}
                            <div className="flex gap-2">
                                <button
                                    className="dh-btn"
                                    data-variant="ghost"
                                    data-testid={`customer-edit-${c.id}`}
                                    onClick={() => openEdit(c)}
                                >
                                    <Pencil size={14} /> Edit
                                </button>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <button
                                            className="dh-btn"
                                            data-variant="danger"
                                            data-testid={`customer-delete-${c.id}`}
                                        >
                                            <Trash2 size={14} /> Delete
                                        </button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent className="bg-white border-2 border-[color:var(--dh-line)] rounded-none">
                                        <AlertDialogHeader>
                                            <AlertDialogTitle className="font-display uppercase">
                                                Delete customer?
                                            </AlertDialogTitle>
                                            <AlertDialogDescription>
                                                This removes {c.name}, their properties and all jobs. This can’t be
                                                undone.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel className="rounded-none border-2 border-[color:var(--dh-line)]">
                                                Cancel
                                            </AlertDialogCancel>
                                            <AlertDialogAction
                                                data-testid={`customer-delete-confirm-${c.id}`}
                                                onClick={() => remove(c.id)}
                                                className="rounded-none border-2 border-[color:var(--dh-line)] bg-[color:var(--dh-red)]"
                                            >
                                                Delete
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                            </div>
                            {(installed || due) ? (
                                <div
                                    className="border-t-2 pt-2 flex flex-wrap justify-center gap-x-6 gap-y-1 text-xs font-mono"
                                    style={{ borderColor: "rgba(2, 132, 199, 0.25)" }}
                                    data-testid={`customer-dates-${c.id}`}
                                >
                                    {installed && (
                                        <span style={{ color: "var(--dh-blue)" }}>
                                            <span className="tracking-widest uppercase font-bold text-[10px]">
                                                Installed
                                            </span>{" "}
                                            {installed}
                                        </span>
                                    )}
                                    {due && (
                                        <span style={{ color: "var(--dh-blue)" }}>
                                            <span className="tracking-widest uppercase font-bold text-[10px]">
                                                Next service due
                                            </span>{" "}
                                            {due}
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <div
                                    className="border-t border-neutral-200 pt-2 text-center text-[10px] tracking-widest uppercase font-bold text-neutral-400"
                                    data-testid={`customer-dates-empty-${c.id}`}
                                >
                                    No install / service dates on file
                                </div>
                            )}
                        </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default Customers;
